import type {
  AuthorQuestionsDraftResponse,
  DisregardGroomingBriefResponse,
  GroomingDisregardUndo,
  UndoDisregardGroomingBriefResponse,
} from "@ttc-spectree/toolbox-contract";
import { api, ApiError } from "./client";

/**
 * Client for the tracker-neutral work-item API (`/api/v1/work-items`,
 * epic PI-SPEC-SW-E276, feature PI-SPEC-SW-1362). Azure DevOps today; the
 * shapes carry no ADO field names, so a GitHub Issues adapter changes nothing here.
 */

export type TrackerKind = "ado";
export type WorkItemKind = "bug" | "story" | "other";
export type StateCategory = "proposed" | "in-progress" | "resolved" | "completed" | "removed";
export type ReadinessTier = "pass" | "advisory" | "critical-fail" | "unsupported";
export type ReadinessLevel = "grooming" | "sprint";
export type WorkItemSectionKey = "description" | "acceptanceCriteria" | "reproSteps" | "systemInfo";

export interface WorkItemRef {
  tracker: TrackerKind;
  /** ADO project name (later a GitHub `owner/repo`). */
  container: string;
  id: string;
  /** Canonical browser URL of the item in the tracker. */
  url: string;
}

/** One rubric rule the item does not meet. */
export interface ReadinessMissing {
  id: string;
  severity: "critical" | "advisory";
  label: string;
  missingMessage: string;
  formulatorSlot: string;
}

/**
 * One rubric rule of the item's kind, met or not (E280): the below-standard
 * pane shows every criterion as present or missing, "N of M in place".
 */
export interface ReadinessCriterion {
  id: string;
  label: string;
  severity: "critical" | "advisory";
  /** True when the item meets the rule. */
  present: boolean;
  /** Why the team needs it, in plain words. */
  explanation: string;
  missingMessage: string;
}

export interface ReadinessLevelResult {
  tier: ReadinessTier;
  /** 0-100. */
  score: number;
  missing: ReadinessMissing[];
  /**
   * Every rule of the item's kind, present or missing (E280). Live reads always
   * carry it; older stored readiness may not, so treat absence as unknown.
   */
  criteria?: ReadinessCriterion[];
}

/** Live readiness of an item; computed by the API on every request. */
export interface WorkItemReadiness {
  rubricVersion: number;
  /** True when the grooming tier is pass or advisory: the item may become an Epic Request. */
  readyForGrooming: boolean;
  grooming: ReadinessLevelResult;
  sprint: ReadinessLevelResult;
}

export interface WorkItemSummary {
  ref: WorkItemRef;
  kind: WorkItemKind;
  typeName: string;
  title: string;
  state: { name: string; category: StateCategory };
  priority?: number;
  severity?: { value: string; isDefault: boolean };
  estimate?: number;
  classification: { area: string; iteration: string };
  tags: string[];
  assignedTo?: { displayName: string; uniqueName?: string; id?: string };
  version: number;
  changedDate: string;
  readiness: WorkItemReadiness;
}

export interface WorkItemSection {
  /** Tracker HTML. Untrusted: always sanitise before rendering. */
  html: string;
  text: string;
}

export interface LinkedEpicRequest {
  id: string;
  identifier: string | null;
  title: string;
  status: string;
  convertedEpicId: string | null;
}

export interface WorkItemDetail extends WorkItemSummary {
  sections: Partial<Record<WorkItemSectionKey, WorkItemSection>>;
  links: { type: string; url: string; comment?: string }[];
  /** `/spectree:ado-to-er <id>`; present only when the item is ready for grooming. */
  convertCommand?: string;
  /** Formulator improve path; present only when the item is not ready. */
  formulatorPath?: string;
  linkedEpicRequest: LinkedEpicRequest | null;
  /**
   * The item's latest grooming brief, read from the same live tracker read
   * (ER-1603 fix B): the item page makes one request and never calls the
   * latest-brief route. Null when the API could not read the brief store.
   */
  groomingBrief?: WorkItemGroomingBriefBlock | null;
  /**
   * Who created the item in the tracker (E292, UX-PM-06): the person the
   * author questions name. Absent when the tracker did not report it, or from
   * an older API.
   */
  createdBy?: { displayName: string; uniqueName?: string; id?: string };
  /** When the item was created (ISO 8601); absent when unknown. */
  createdDate?: string;
  /**
   * The newest ask of the item's author, from this same read (E292,
   * ER-1603 fix B: one item read, one ADO read); null when the author was
   * never asked, absent when SpecTree could not read its asks (then whether
   * the author was asked is unknown, and the pane offers no Post).
   */
  authorAsk?: LatestAuthorQuestionAsk | null;
}

export interface WorkItemProject {
  tracker: TrackerKind;
  id: string;
  name: string;
  processName: string | null;
}

export interface MyWorkItemsFilters {
  /** Restrict to these projects; empty or omitted means every Agile-family project. */
  projects?: string[];
  kind?: "bug" | "story";
}

// ---------------------------------------------------------------------------
// Grooming briefs (epic PI-SPEC-SW-E277). Written by the in-session
// `/spectree:ado-groom` command or the batch engine (E279); the web reads
// them and (E279) posts or skips held ones. Every string is untrusted model
// output: render as text, never as HTML.
// ---------------------------------------------------------------------------

export type GroomingConfidence = "low" | "medium" | "high";
/**
 * Outcome of posting the brief comment to the tracker. `held` (E279): stored
 * in SpecTree, not posted to the tracker yet; a person reviews and posts it.
 * `skipped`: a person decided the held brief is never posted.
 */
export type GroomingCommentStatus = "pending" | "posted" | "updated" | "reposted" | "failed" | "held" | "skipped";

/** How much of a product area the change touches. */
export type GroomingAreaLevel = "main" | "small" | "check";

/** One product area the plain brief says the change touches. */
export interface GroomingPlainBriefArea {
  area: string;
  level: GroomingAreaLevel;
  what: string;
}

/** The plain-language brief for a mixed grooming room (E279). SpecTree-only, never in the ADO comment. */
export interface GroomingPlainBrief {
  whatIsIt: string;
  whoNotices: string;
  areasTouched: GroomingPlainBriefArea[];
  sizeInWords: string;
  comparableInWords?: string;
  worthKnowing: string[];
  askTheTeam: string[];
}

/** One reason for the suggested size and what it does to it. */
export interface GroomingSizeDriver {
  driver: string;
  /** `+` adds size, `-` removes size, `uncertainty` adds risk of growth. */
  effect: "+" | "-" | "uncertainty";
  detail?: string;
}

/** A closed, pointed item from the team's own history. */
export interface GroomingComparable {
  id: string;
  title: string;
  points: number;
  why: string;
}

export interface GroomingEngineerNotes {
  files: string[];
  evidence: string[];
  confirmed: string[];
  inferred: string[];
  /** Bugs only: the likely cause, always unverified. */
  bugHypothesis?: string;
}

/** A repository named in the brief body. */
export interface GroomingBriefBodyRepo {
  name: string;
  commit: string;
  /** False when the work touches this repository but no checkout was read. */
  examined: boolean;
}

/** The brief as the skill wrote it (mirrors the API POST body). */
export interface GroomingBriefBody {
  plainSummary: string;
  whatChanges: string[];
  sizeDrivers: GroomingSizeDriver[];
  suggestedPoints: number;
  pointsLow: number;
  pointsHigh: number;
  confidence: GroomingConfidence;
  splitSuggestion?: string;
  calibrated: boolean;
  comparables: GroomingComparable[];
  questions: string[];
  engineerNotes: GroomingEngineerNotes;
  repos: GroomingBriefBodyRepo[];
  itemVersion: number;
  generator: string;
  /** The plain-language brief, when the run wrote one (E279). */
  plainBrief?: GroomingPlainBrief;
}

/** A repository the stored brief analysed. */
export interface GroomingBriefRepo {
  project?: string;
  repo: string;
  branch?: string;
  commit?: string;
  examined?: boolean;
}

export interface GroomingBriefAuthor {
  id: string;
  name: string;
}

/** The latest stored brief for an item. */
export interface GroomingBrief {
  id: string;
  createdAt: string;
  createdBy?: GroomingBriefAuthor | null;
  suggestedPoints: number;
  pointsLow: number;
  pointsHigh: number;
  confidence: GroomingConfidence;
  calibrated: boolean;
  commentStatus: GroomingCommentStatus;
  adoCommentId?: string | null;
  /** The item's estimate when the brief was saved (set before the brief), or null. */
  estimateAtSave?: number | null;
  /** Who posted a held brief to ADO, and when (E279); null until posted. */
  postedBy?: GroomingBriefAuthor | null;
  postedAt?: string | null;
  body: GroomingBriefBody;
  repos: GroomingBriefRepo[];
}

/** One stored version of an item's brief, metadata only. */
export interface GroomingBriefHistoryEntry {
  id: string;
  createdAt: string;
  createdBy: GroomingBriefAuthor | null;
  suggestedPoints: number;
  pointsLow: number;
  pointsHigh: number;
  confidence: GroomingConfidence;
  commentStatus: GroomingCommentStatus;
  teamPoints: number | null;
}

/** `GET /work-items/:tracker/:container/:id/grooming-briefs/latest`. */
export interface LatestGroomingBriefResponse {
  brief: GroomingBrief | null;
  /** True when the item's content changed since the brief was written. */
  stale: boolean;
  /** The team's estimate after the brief; never an estimate set before it. */
  teamPoints: number | null;
  teamPointsSeenAt: string | null;
  /** The item's estimate right now, read live. */
  liveEstimate: number | null;
  itemUrl?: string;
  /** Every stored version, newest first. */
  history: GroomingBriefHistoryEntry[];
}

/**
 * The brief block on the item read (`WorkItemDetail.groomingBrief`): the
 * latest-brief response without the item URL, plus the estimate the item had
 * when the brief was saved. Exported for the E279 item summary page, which
 * reads its brief from the same item query.
 */
export interface WorkItemGroomingBriefBlock extends Omit<LatestGroomingBriefResponse, "itemUrl"> {
  estimateAtSave: number | null;
}

/** Outcome of the tracker comment step of a post. */
export interface GroomingCommentOutcome {
  status: Exclude<GroomingCommentStatus, "pending">;
  commentId?: string;
  error?: { code: string; message: string };
}

/** `POST .../grooming-briefs/:briefId/post`. */
export interface PostHeldBriefResponse {
  briefId: string;
  commentStatus: GroomingCommentStatus;
  adoCommentId: string | null;
  postedById: string | null;
  postedAt: string | null;
  /** True when posted with `force` although the item changed. */
  stale: boolean;
  comment: GroomingCommentOutcome;
}

/** `POST .../grooming-briefs/:briefId/skip`. */
export interface SkipHeldBriefResponse {
  briefId: string;
  reviewState: "skipped";
  batchItemIds: string[];
}

/** `POST .../grooming-briefs/:briefId/unskip` (E280, Hold back's Undo). */
export interface UnskipHeldBriefResponse {
  briefId: string;
  reviewState: "pending";
  /** The batch items that went back to `pending`. */
  batchItemIds: string[];
}

/**
 * `PUT` / `GET .../author-questions/draft` (E280): questions SpecTree Toolbox
 * drafted for the item's author. Untrusted model output: render as text.
 */
export type AuthorQuestionsDraft = AuthorQuestionsDraftResponse;

/** What a disregard changed (grooming contract 5); the body of the undo route. Held in memory only. */
export type { DisregardGroomingBriefResponse, GroomingDisregardUndo, UndoDisregardGroomingBriefResponse };

/** `GET .../grooming-briefs/:briefId/comment-preview`: the exact HTML a post would send. */
export interface BriefCommentPreviewResponse {
  briefId: string;
  /** Escaped server-side HTML; render only through the page's sanitiser. */
  html: string;
  stale: boolean;
}

/**
 * One stored ask (epic PI-SPEC-SW-E292, UX-PM-06): a set of author questions
 * posted to the tracker as one comment. Every post is stored; Ask again is
 * another post (a new comment and a new ask). `questions` are untrusted model
 * output, possibly edited by the poster: render as text only.
 */
export interface AuthorQuestionAsk {
  id: string;
  itemId: string;
  /** The item's project when the questions were posted (informational). */
  container: string;
  /** The questions exactly as posted, in order. */
  questions: string[];
  adoCommentId: string;
  /** True when the comment opened with an ADO @mention of the item's creator. */
  mentioned: boolean;
  /** The creator's display name, when mentioned. */
  mentionedName: string | null;
  /** Null once the poster's SpecTree account is deleted. */
  postedById: string | null;
  /** The poster's SpecTree name. */
  postedByName: string | null;
  /** When the questions were posted (ISO 8601). */
  createdAt: string;
}

/**
 * `GET .../author-questions/latest-ask` (E292): the newest ask, how many times
 * the item's author was asked and whether the item changed since. The API
 * answers `data: null` when the author was never asked.
 */
export interface LatestAuthorQuestionAsk extends AuthorQuestionAsk {
  /** Every post counts. */
  askCount: number;
  /** True when the item's content changed after the newest ask (an estimate change alone never counts). */
  changedSinceAsk: boolean;
  /** The item's last change (ISO 8601) when `changedSinceAsk`, else null. */
  itemChangedAt: string | null;
}

/** `POST .../author-questions`. */
export interface AuthorQuestionsResponse {
  commentId: string;
  itemUrl: string;
  itemPagePath: string;
  /** E292: true when the comment opened with the creator's @mention. Absent from an older API. */
  mentioned?: boolean;
  /** E292: the creator's display name, when mentioned. */
  mentionedName?: string | null;
  /**
   * E292: the stored ask. Null when the comment posted but the record could
   * not be stored; never post again to recover. Absent from an older API.
   */
  ask?: AuthorQuestionAsk | null;
}

/** The item a brief route addresses. */
export interface WorkItemAddress {
  tracker: TrackerKind;
  container: string;
  id: string;
}

interface DataResponse<T> {
  data: T;
}

function itemPath(item: WorkItemAddress): string {
  return `/work-items/${encodeURIComponent(item.tracker)}/${encodeURIComponent(item.container)}/${encodeURIComponent(item.id)}`;
}

function mineQuery(filters: MyWorkItemsFilters): string {
  const params = new URLSearchParams();
  if (filters.projects && filters.projects.length > 0) params.set("projects", filters.projects.join(","));
  if (filters.kind) params.set("kind", filters.kind);
  const query = params.toString();
  return query ? `?${query}` : "";
}

export const workItemsApi = {
  /** Open bugs and stories assigned to the caller, each with live readiness. */
  mine: (filters: MyWorkItemsFilters = {}) =>
    api.get<DataResponse<WorkItemSummary[]>>(`/work-items/mine${mineQuery(filters)}`),

  /** One item with live readiness and either the convert command or the Formulator path. */
  detail: (tracker: TrackerKind, container: string, id: string) =>
    api.get<DataResponse<WorkItemDetail>>(
      `/work-items/${encodeURIComponent(tracker)}/${encodeURIComponent(container)}/${encodeURIComponent(id)}`
    ),

  /** The latest grooming brief for an item, its staleness and version history. */
  latestGroomingBrief: (tracker: TrackerKind, container: string, id: string) =>
    api.get<DataResponse<LatestGroomingBriefResponse>>(
      `/work-items/${encodeURIComponent(tracker)}/${encodeURIComponent(container)}/${encodeURIComponent(id)}/grooming-briefs/latest`
    ),

  /** Post a held (or failed) brief to ADO as the caller. `force` posts although the item changed. */
  postHeldBrief: (item: WorkItemAddress, briefId: string, options: { force?: boolean } = {}) =>
    api.post<DataResponse<PostHeldBriefResponse>>(
      `${itemPath(item)}/grooming-briefs/${encodeURIComponent(briefId)}/post`,
      options.force ? { force: true } : {}
    ),

  /** Undo a skip (Hold back's Undo): the brief is held again and its batch item back to pending. 409 once posted. */
  unskipHeldBrief: (item: WorkItemAddress, briefId: string) =>
    api.post<DataResponse<UnskipHeldBriefResponse>>(
      `${itemPath(item)}/grooming-briefs/${encodeURIComponent(briefId)}/unskip`,
      {}
    ),

  /**
   * The latest drafted questions for the item's author, with `isCurrent`
   * false once the item changed. 404 (`AUTHOR_QUESTIONS_DRAFT_NOT_FOUND`)
   * when none were drafted; {@link useAuthorQuestionsDraft} maps that to null.
   */
  getAuthorQuestionsDraft: (item: WorkItemAddress) =>
    api.get<DataResponse<AuthorQuestionsDraft>>(`${itemPath(item)}/author-questions/draft`),

  /**
   * Store drafted questions for the item's author. The Toolbox main process is
   * the normal writer; the web uses this only as a browser fallback and in tests.
   */
  saveAuthorQuestionsDraft: (item: WorkItemAddress, questions: string[]) =>
    api.put<DataResponse<AuthorQuestionsDraft>>(`${itemPath(item)}/author-questions/draft`, { questions }),

  /** Skip a held brief: never posted; its batch item is marked skipped. */
  skipHeldBrief: (item: WorkItemAddress, briefId: string) =>
    api.post<DataResponse<SkipHeldBriefResponse>>(
      `${itemPath(item)}/grooming-briefs/${encodeURIComponent(briefId)}/skip`,
      {}
    ),

  /**
   * Disregard an unposted (held, skipped or failed) brief (grooming contract
   * 5): the brief is discarded and its batch items go back to "needs brief".
   * 409 `GROOMING_BRIEF_POSTED` once posted. The returned `undo` is the body
   * for {@link workItemsApi.undoDisregardBrief}; keep it in memory only.
   */
  disregardBrief: (item: WorkItemAddress, briefId: string) =>
    api.post<DataResponse<DisregardGroomingBriefResponse>>(
      `${itemPath(item)}/grooming-briefs/${encodeURIComponent(briefId)}/disregard`,
      {}
    ),

  /** Undo a disregard with the `undo` it returned. 409 `GROOMING_DISREGARD_UNDO_UNAVAILABLE` once the item moved on. */
  undoDisregardBrief: (item: WorkItemAddress, briefId: string, undo: GroomingDisregardUndo) =>
    api.post<DataResponse<UndoDisregardGroomingBriefResponse>>(
      `${itemPath(item)}/grooming-briefs/${encodeURIComponent(briefId)}/undo-disregard`,
      undo
    ),

  /** The exact comment HTML a post of this brief would send. No ADO write. */
  briefCommentPreview: (item: WorkItemAddress, briefId: string) =>
    api.get<DataResponse<BriefCommentPreviewResponse>>(
      `${itemPath(item)}/grooming-briefs/${encodeURIComponent(briefId)}/comment-preview`
    ),

  /** Post questions for the author of an item that is not ready for grooming. */
  postAuthorQuestions: (item: WorkItemAddress, questions: string[]) =>
    api.post<DataResponse<AuthorQuestionsResponse>>(`${itemPath(item)}/author-questions`, { questions }),

  /** Projects My Items covers. */
  projects: () => api.get<DataResponse<WorkItemProject[]>>("/work-items/projects"),
};

/**
 * Error codes the work-item API uses for tracker credential problems. Both
 * mean "fix your PAT in Settings", never a SpecTree logout.
 */
export const ADO_PAT_ERROR_CODES = ["ADO_PAT_INVALID", "ADO_PAT_REQUIRED"] as const;
export type AdoPatErrorCode = (typeof ADO_PAT_ERROR_CODES)[number];

/** The API error code (`error.code`) of a failed request, when there is one. */
export function getApiErrorCode(error: unknown): string | undefined {
  if (!(error instanceof ApiError)) return undefined;
  const data = error.data;
  if (data && typeof data === "object" && "error" in data) {
    const code = (data as { error?: { code?: unknown } }).error?.code;
    return typeof code === "string" ? code : undefined;
  }
  return undefined;
}

/** The PAT error code of a failed request, or `undefined` for any other failure. */
export function getAdoPatErrorCode(error: unknown): AdoPatErrorCode | undefined {
  const code = getApiErrorCode(error);
  return (ADO_PAT_ERROR_CODES as readonly string[]).includes(code ?? "")
    ? (code as AdoPatErrorCode)
    : undefined;
}

/** The web route of an item's page. */
export function workItemPagePath(ref: Pick<WorkItemRef, "container" | "id">): string {
  return `/ado/items/${encodeURIComponent(ref.container)}/${encodeURIComponent(ref.id)}`;
}

/**
 * Router state for links to {@link workItemPagePath} that must stay on item
 * detail (My ADO Items, the Formulator, the palette's no-role row). Without
 * it a person with `read-backlog` on the project, following the link from a
 * posted ADO comment, is sent to the item in Grooming (epic PI-SPEC-SW-E292,
 * feature PI-SPEC-SW-1495, UX-PM-17).
 */
export const ITEM_DETAIL_STATE = { keepItemDetail: true } as const;

/** True when router state asks to stay on item detail ({@link ITEM_DETAIL_STATE}). Pure. */
export function keepsItemDetail(state: unknown): boolean {
  return typeof state === "object" && state !== null && (state as { keepItemDetail?: unknown }).keepItemDetail === true;
}
